import React, { useState, useRef, useEffect } from 'react';
import './PremiumSelect.scss';

interface Option {
  value: string;
  label: string;
  /**
   * Optional shorter text for the CLOSED control. Dial codes use it so the field reads "+44"
   * while the list keeps "+44 (United Kingdom)" — the name is what makes 200+ options findable,
   * but it does not fit, and is not wanted, in the box itself.
   */
  selectedLabel?: string;
}

interface PremiumSelectProps {
  value: string;
  onChange?: (value: string) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  error?: string;
  id?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  /** Opt-in: shows a filter box inside the dropdown for long lists (e.g. countries). */
  searchable?: boolean;
}

export const PremiumSelect: React.FC<PremiumSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select',
  className = '',
  error,
  id,
  style,
  disabled = false,
  searchable = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  // Filter as the user types; names STARTING with the query rank above ones that
  // merely contain it (typing "i" puts India/Indonesia before Fiji).
  const q = query.trim().toLowerCase();
  const visibleOptions = searchable && q
    ? options
        .filter((o) => o.label.toLowerCase().includes(q))
        .sort((a, b) =>
          Number(b.label.toLowerCase().startsWith(q)) - Number(a.label.toLowerCase().startsWith(q)))
    : options;

  // Focus the filter box when the dropdown opens; clear the query when it closes.
  useEffect(() => {
    if (isOpen && searchable) {
      searchRef.current?.focus();
    }
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen, searchable]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [disabled]);

  const handleSelect = (optionValue: string) => {
    if (onChange) {
      onChange(optionValue);
    }
    setIsOpen(false);
  };

  return (
    <div 
      id={id}
      className={`premium-select-container ${className} ${isOpen ? 'is-open' : ''} ${error ? 'is-invalid' : ''} ${disabled ? 'is-disabled' : ''}`}
      ref={dropdownRef}
      style={style}
    >
      <div 
        className="premium-select-trigger" 
        onClick={() => !disabled && setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-disabled={disabled}
      >
        <span className={`selected-value ${!selectedOption ? 'placeholder' : ''}`}>
          {selectedOption ? (selectedOption.selectedLabel ?? selectedOption.label) : placeholder}
        </span>
        <i className={`bi bi-chevron-down arrow-icon ${isOpen ? 'rotate' : ''}`}></i>
      </div>

      {isOpen && (
        <div className="premium-select-dropdown">
          {searchable && (
            <div className="premium-select-search" onClick={(e) => e.stopPropagation()}>
              <i className="bi bi-search" aria-hidden="true"></i>
              <input
                ref={searchRef}
                type="text"
                data-keep-case
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsOpen(false);
                  // Enter picks the top match — typing "ind" + Enter selects India.
                  // ALWAYS preventDefault: the select usually sits inside a <form>, and
                  // an unhandled Enter (e.g. zero matches) would submit that form.
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (visibleOptions.length > 0) handleSelect(visibleOptions[0].value);
                  }
                }}
                placeholder="Type to search…"
                aria-label="Search options"
              />
            </div>
          )}
          <ul className="premium-select-options" role="listbox">
            {visibleOptions.map((option, i) => (
              <li
                /* Index-suffixed: two options can legitimately share a value (a duplicated
                   master row), and a repeated key makes React drop or mis-render rows. */
                key={`${option.value}::${i}`}
                className={`premium-select-option ${option.value === value ? 'is-selected' : ''}`}
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={option.value === value}
              >
                {option.label}
              </li>
            ))}
            {visibleOptions.length === 0 && (
              <li className="premium-select-option is-empty" aria-disabled="true">No matches</li>
            )}
          </ul>
        </div>
      )}
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </div>
  );
};
